/* Park Friends: edit the name and number on a cart line, in the drawer.
   ---------------------------------------------------------------------------------------
   The jerseys are personalised on the PDP with a name and a number that go on the back, and
   until now the only way to fix a typo was to remove the line and build it again from the
   product page. This lets the customer change both in place.

   How the write is done, and why this way

   Shopify's cart has no "update the properties of a line" call in the sense of a merge, but
   POST /cart/change.js does accept a `properties` object and applies it to the line at a
   given position. That is the documented route and it is the one used here. Two consequences
   drive the rest of this file:

     1. `properties` REPLACES the line's whole property set. Anything not posted is dropped.
        So the panel sends the line's original properties, merged with the edited fields, and
        that original set is carried in data-pf-line-props including the hidden underscore
        keys and the production-time consent the customer agreed to.

     2. Changing properties changes the line's key, because the key is derived from the
        variant and the properties. The line is therefore addressed by `line`, its 1-based
        position, never by key. Position and quantity both survive; nothing else moves.

   A remove-and-re-add was the alternative and was rejected: add.js appends, so the edited
   jersey would jump to the bottom of the drawer, and a failure between the two calls would
   leave the customer's cart short an item. change.js is one request that either lands or does
   not.

   Everything is delegated from the document, because assets/cart.js replaces .offcanvas-body
   wholesale on every cart change and a listener bound to a line would not survive it. */
(function () {
  'use strict';

  var BUSY = false;

  function panelOf(el) { return el.closest('.pf-line')?.querySelector('[data-pf-edit-panel]'); }

  /* Liquid's json filter renders line_item.properties as an ARRAY of [key, value] pairs, not
     as an object. Reading it as an object gave undefined for every key, which is how the first
     pass silently dropped the production-time consent off an edited line. Both shapes are
     accepted here so the attribute can be read safely wherever it is used. */
  function readProps(raw) {
    var parsed;
    try { parsed = JSON.parse(raw || '{}'); } catch (e) { return {}; }
    if (Array.isArray(parsed)) {
      return parsed.reduce(function (acc, pair) {
        if (Array.isArray(pair) && pair.length === 2) acc[pair[0]] = pair[1];
        return acc;
      }, {});
    }
    return parsed && typeof parsed === 'object' ? parsed : {};
  }

  function showError(panel, message) {
    var box = panel.querySelector('[data-pf-edit-error]');
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
  }

  function clearError(panel) {
    var box = panel.querySelector('[data-pf-edit-error]');
    if (box) { box.textContent = ''; box.hidden = true; }
  }

  function openPanel(btn) {
    var line = btn.closest('.pf-line');
    var panel = line && line.querySelector('[data-pf-edit-panel]');
    if (!panel) return;
    var opening = panel.hidden;
    panel.hidden = !opening;
    btn.setAttribute('aria-expanded', String(opening));
    if (opening) {
      clearError(panel);
      /* The panel is taller than the line, so on a phone it opens below the fold of the
         scroller. Bring it into view before the field takes focus. */
      panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      var first = panel.querySelector('.pf-line__edit-input');
      if (first) { first.focus(); first.select(); }
    }
  }

  /* Cancel puts the fields back to what the line actually holds, so reopening the panel never
     shows a half-typed value that was never saved. */
  function cancelPanel(btn) {
    var line = btn.closest('.pf-line');
    var panel = line && line.querySelector('[data-pf-edit-panel]');
    if (!panel) return;
    var original = readProps(line.dataset.pfLineProps);
    panel.querySelectorAll('.pf-line__edit-input').forEach(function (input) {
      var key = input.dataset.pfProp;
      input.value = original[key] == null ? '' : String(original[key]);
    });
    clearError(panel);
    panel.hidden = true;
    var toggle = line.querySelector('[data-pf-edit-toggle]');
    if (toggle) { toggle.setAttribute('aria-expanded', 'false'); toggle.focus(); }
  }

  async function savePanel(btn) {
    if (BUSY) return;
    var line = btn.closest('.pf-line');
    var panel = line && line.querySelector('[data-pf-edit-panel]');
    if (!panel) return;
    clearError(panel);

    /* The PDP's rules, applied again here: the length cap the customiser set, digits only on a
       number field, and no emptying a field the customer had already filled in. A jersey that
       reached the cart with a name on it must not be able to leave the drawer without one. */
    var edits = {};
    var inputs = panel.querySelectorAll('.pf-line__edit-input');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var max = parseInt(input.getAttribute('maxlength'), 10);
      var value = String(input.value).trim();
      if (input.hasAttribute('data-pf-digits')) value = value.replace(/\D+/g, '');
      if (max > 0) value = value.slice(0, max);
      input.value = value;
      if (input.hasAttribute('data-pf-required') && value === '') {
        var label = input.closest('.pf-line__edit-field')?.querySelector('.pf-line__edit-label')?.textContent.trim() || 'This field';
        showError(panel, label + ' cannot be left empty.');
        input.focus();
        return;
      }
      edits[input.dataset.pfProp] = value;
    }

    var index = parseInt(line.dataset.pfLineIndex, 10);
    var variantId = String(line.dataset.pfLineVariant || '');
    if (!(index > 0)) { showError(panel, 'This item could not be found in your cart. Reload the page and try again.'); return; }

    BUSY = true;
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Saving';

    try {
      /* Read the live cart first. The drawer's position and quantity come from the render, and
         a second tab or a stale drawer could have moved the line since. Writing the wrong
         position would rewrite somebody else's jersey, so the variant has to match before the
         change goes out, and the quantity comes from the cart rather than the markup. */
      var current = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!current.ok) throw new Error('cart read failed');
      var cart = await current.json();
      var live = cart.items[index - 1];
      if (!live || String(live.variant_id) !== variantId) {
        showError(panel, 'Your cart changed in another window. Reload the page and edit it again.');
        return;
      }

      /* The base is the LIVE line's property set, not the one rendered into the markup.
         change.js replaces properties outright, so every key the customer already agreed to
         has to be posted back: the production-time consent, and any hidden underscore key an
         app put there. Reading them off the cart a moment before the write is the only version
         that cannot be stale, and a stale base here would silently drop a property from the
         order rather than fail loudly. */
      var next = Object.assign({}, live.properties || {}, edits);

      var response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: index, quantity: live.quantity, properties: next })
      });

      if (!response.ok) {
        var detail = '';
        try { var data = await response.json(); detail = data.description || data.message || ''; } catch (e) {}
        showError(panel, detail || 'That change did not save. Your item is unchanged.');
        return;
      }

      /* cart.js reads the body only for add.js, so handing it an untouched change.js response
         is safe and gives the same re-render every other cart action uses. */
      window.refreshCartContents(response);
    } catch (e) {
      showError(panel, 'That change did not save. Your item is unchanged.');
    } finally {
      BUSY = false;
      btn.disabled = false;
      btn.textContent = label;
    }
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest?.('[data-pf-edit-toggle]');
    if (toggle) { event.preventDefault(); openPanel(toggle); return; }
    var cancel = event.target.closest?.('[data-pf-edit-cancel]');
    if (cancel) { event.preventDefault(); cancelPanel(cancel); return; }
    var save = event.target.closest?.('[data-pf-edit-save]');
    if (save) { event.preventDefault(); savePanel(save); }
  });

  /* Enter saves, Escape closes: the panel is two short text fields and a keyboard is the
     fastest way through it on desktop. */
  document.addEventListener('keydown', function (event) {
    var input = event.target.closest?.('.pf-line__edit-input');
    if (!input) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      var save = panelOf(input)?.querySelector('[data-pf-edit-save]');
      if (save) savePanel(save);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      var cancel = panelOf(input)?.querySelector('[data-pf-edit-cancel]');
      if (cancel) cancelPanel(cancel);
    }
  });
})();
